import React, { useEffect, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { buildSystemPrompt } from "./Prompt";
import { streamGeneration, getProviderInfo } from "./Inference";
import TextSelectionPopup from "./TextSelectionPopup";
import DiffView from "./DiffView";
import Changelog from "./Changelog";
import "./App.css";

const getContractBasePath = () => {
  // Access via string key to prevent bundler optimization
  return window['CONTRACT_BASE_PATH'] || '/contracts/';
};

// Contract and role configuration
const CONTRACTS = {
  "microsoft-ibm-joint-development.txt": {
    name: "Microsoft-IBM Joint Development Agreement",
    roles: ["Microsoft", "IBM"]
  },
  "nba-uniform-player-contract.txt": {
    name: "NBA Uniform Player Contract",
    roles: ["Player", "Team"]
  },
  "un-charter.txt": {
    name: "UN Charter",
    roles: ["For", "Against"]
  }
};

// Minimal Tiptap editor that renders text on a page
// Drop this into your App.jsx in a fresh React project
export default function App() {
  const [contractContent, setContractContent] = useState("Loading contract...");
  const [selectedContract, setSelectedContract] = useState("microsoft-ibm-joint-development.txt");
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Combined popup state for better organization
  const [popupState, setPopupState] = useState({
    basePosition: null,      // Initial calculated position from selection
    position: null,          // Current position (after dragging)
    selectedText: "",
    selectionRange: null
  });
  
  const popupTimeoutRef = useRef(null);
  const [diffView, setDiffView] = useState(null);
  const [customContext, setCustomContext] = useState("");
  const [userRole, setUserRole] = useState("Microsoft"); // Default role based on selected contract
  const [changes, setChanges] = useState([]);
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelogPosition, setChangelogPosition] = useState(null);
  const [animationState, setAnimationState] = useState('pending'); // 'pending' | 'running' | 'complete'

  // Configurable animation parameters - makes the startup animation system generic and reusable
  const ANIMATION_CONFIG = {
    targetStartCharacterPos: 566, // Character position to start animation from (0-based)
    maxSelectionLength: 225,      // Maximum characters to select from starting position
    animationSteps: 10,          // Number of steps for the gradual selection animation
    stepDelay: 40,               // Milliseconds between each animation step
    initialDelay: 300            // Initial delay before starting selection expansion
  };

  // Function to find animation target based on character position
  const findAnimationTarget = () => {
    if (!editor || editor.isDestroyed) return null;

    const { state } = editor;
    const { doc } = state;

    const targetPos = ANIMATION_CONFIG.targetStartCharacterPos;

    // Find the node at the target character position
    let targetNode = null;
    doc.descendants((node, pos) => {
      if (pos <= targetPos && pos + node.textContent.length > targetPos) {
        targetNode = node;
        return false; // Stop traversing once we find the target node
      }
    });

    return { position: targetPos, node: targetNode };
  };

  // Function to perform startup animation with configurable character position targeting
  const performStartupAnimation = () => {
    if (!editor || editor.isDestroyed || animationState !== 'pending') return;

    setAnimationState('running');

    try {
      const target = findAnimationTarget();

      if (target && target.position !== null) {
        const animationStartPos = target.position;
        const maxLength = ANIMATION_CONFIG.maxSelectionLength;

        // Calculate the end position (limit by max length and document bounds)
        const { state } = editor;
        const { doc } = state;
        const totalLength = doc.textContent.length;
        const animationEndPos = Math.min(animationStartPos + maxLength, totalLength);

        // Use setTimeout to ensure the content is fully rendered
        setTimeout(() => {
          if (editor && !editor.isDestroyed) {
            // Start with cursor at the target position
            editor.chain().focus().setTextSelection({
              from: animationStartPos,
              to: animationStartPos
            }).run();

            // Gradually expand selection like a human dragging
            let currentEnd = animationStartPos + 1;
            const expandStep = Math.max(1, Math.floor(maxLength / ANIMATION_CONFIG.animationSteps));
            const stepDelay = ANIMATION_CONFIG.stepDelay;

            const expandSelection = () => {
              currentEnd = Math.min(animationEndPos, currentEnd + expandStep);

              if (currentEnd < animationEndPos && editor && !editor.isDestroyed) {
                editor.chain().setTextSelection({
                  from: animationStartPos,
                  to: currentEnd
                }).run();

                setTimeout(expandSelection, stepDelay);
              } else {
                // Selection complete
                editor.chain().setTextSelection({
                  from: animationStartPos,
                  to: animationEndPos
                }).run();

                // Mark animation complete - popup can now show
                setAnimationState('complete');

                // Trigger popup display immediately after animation completes
                const { view, state } = editor;
                const { from, to } = state.selection;
                const start = view.coordsAtPos(from);
                const end = view.coordsAtPos(to);

                // Get editor bounds
                const editorElement = document.querySelector('.editor-wrapper');
                const editorRect = editorElement?.getBoundingClientRect();

                // Position popup to the left of editor
                const popupWidth = 200; // Match TextSelectionPopup min-width
                const gap = 40; // Space between editor and popup
                const x = editorRect ? editorRect.left - popupWidth - gap : start.left;

                // Calculate centered Y position for the popup
                const selectionCenterY = (start.top + end.top) / 2;
                const estimatedPopupHeight = 240; // Estimated popup height based on CSS
                const halfPopupHeight = estimatedPopupHeight / 2;
                const y = (start.top + end.bottom) / 2 - 120;

                // Set popup state with both base and current position
                const selectedHtml = state.doc.textBetween(from, to, " ");
                const text = selectedHtml.replace(/<br\s*\/?>/gi, '\n').trim();
                setPopupState({
                  basePosition: { x, y },
                  position: { x, y },
                  selectedText: text,
                  selectionRange: { from, to }
                });
              }
            };

            // Start expanding after initial delay
            setTimeout(expandSelection, ANIMATION_CONFIG.initialDelay);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error in startup animation:', error);
      setAnimationState('complete');
    }
  };

  useEffect(() => {
    const basePath = getContractBasePath();
    
    // Fetch the contract text content
    fetch(basePath + selectedContract)
      .then(response => response.text())
      .then(text => {
        // Convert newlines to HTML breaks for proper formatting
        const formattedText = text.replace(/\n/g, '<br>');
        setContractContent(formattedText);
      })
      .catch(error => {
        console.error('Error loading contract:', error);
        setContractContent("Error loading contract content.");
      });
  }, [selectedContract]);

  // Update user role when contract changes
  useEffect(() => {
    const contractInfo = CONTRACTS[selectedContract];
    if (contractInfo && contractInfo.roles.length > 0) {
      setUserRole(contractInfo.roles[0]);
    }
  }, [selectedContract]);

  const editor = useEditor({
    extensions: [
      StarterKit,
    ],
    content: contractContent,
    editorProps: {
      attributes: {
        spellCheck: "true",
      },
    },
  });

  // Update editor content when contractContent changes
  useEffect(() => {
    if (editor && contractContent) {
      editor.commands.setContent(contractContent);

      // Use setTimeout to ensure content is fully rendered before focusing
      // This fixes the issue in production builds where timing can be different
      setTimeout(() => {
        if (editor && editor.isDestroyed === false) {
          // Perform startup animation on first load
          performStartupAnimation();
        }
      }, 50);
    }
  }, [editor, contractContent]);

  // Handle text selection to show popup
  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      // Clear any pending timeout
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }

      const { state } = editor;
      const { from, to } = state.selection;
      const selectedHtml = state.doc.textBetween(from, to, " ");
      // Strip HTML tags for processing while keeping the text content
      const text = selectedHtml.replace(/<br\s*\/?>/gi, '\n').trim();

      // Don't show popup during startup animation
      const canShowPopup = animationState === 'complete';

      if (text.length > 0 && canShowPopup) {
        // Delay showing the popup until cursor stops moving
        popupTimeoutRef.current = setTimeout(() => {
          // Get the coordinates of the selection
          const { view } = editor;
          const start = view.coordsAtPos(from);
          const end = view.coordsAtPos(to);
          
          // Get editor bounds
          const editorElement = document.querySelector('.editor-wrapper');
          const editorRect = editorElement?.getBoundingClientRect();

          // Position popup to the left of editor
          const popupWidth = 200; // Match TextSelectionPopup min-width
          const gap = 40; // Space between editor and popup
          const x = editorRect ? editorRect.left - popupWidth - gap : start.left;

          // Calculate centered Y position for the popup
          const selectionCenterY = (start.top + end.top) / 2;
          const estimatedPopupHeight = 240; // Estimated popup height based on CSS
          const halfPopupHeight = estimatedPopupHeight / 2;
          const y = (start.top + end.bottom) / 2 - 120;

          setPopupState({
            basePosition: { x, y },
            position: { x, y },
            selectedText: text,
            selectionRange: { from, to }
          });
        }, 300); // 300ms delay
      } else {
        // Hide popup immediately when selection is cleared
        setPopupState({
          basePosition: null,
          position: null,
          selectedText: "",
          selectionRange: null
        });
      }
    };

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      // Clean up any pending timeout
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, [editor, animationState]);

  // Hide popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside the editor and popup
      if (!e.target.closest('.ProseMirror') && !e.target.closest('.text-selection-popup')) {
        setPopupState({
          basePosition: null,
          position: null,
          selectedText: "",
          selectionRange: null
        });
      }

      // Close diff view when clicking outside of it
      if (diffView && !e.target.closest('.diff-view-container')) {
        setDiffView(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [diffView]);

  // Handle scroll events to update component positions
  useEffect(() => {
    const handleScroll = () => {
      const editorElement = document.querySelector('.ProseMirror');
      if (!editorElement) return;

      const editorRect = editorElement.getBoundingClientRect();

      // Update Changelog position if it's visible
      if (showChangelog && changelogPosition) {
        const newPosition = calculateChangelogPosition();
        if (newPosition) {
          setChangelogPosition(newPosition);
        }
      }

      // For TextSelectionPopup, check if editor is visible and update position accordingly
      if (popupState.position) {
        const isEditorVisible = editorRect.top < window.innerHeight && editorRect.bottom > 0;

        if (!isEditorVisible) {
          // Hide popup when editor is out of view
          setPopupState(prev => ({
            ...prev,
            basePosition: null,
            position: null,
            selectedText: "",
            selectionRange: null
          }));
        } else {
          // Keep the same X coordinate and only update Y coordinate to stay within boundary
          const editorHeight = editorRect.bottom - editorRect.top;
          const relativeY = popupState.position.y - editorRect.top;
          const newY = editorRect.top + Math.max(10, Math.min(relativeY, editorHeight - 100));

          setPopupState(prev => ({
            ...prev,
            position: { x: prev.position.x, y: newY }
          }));
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [popupState.position, showChangelog, changelogPosition]);


  const handleGenerateVersions = async (selectedVersion) => {
    const { selectedText, selectionRange, position } = popupState;
    
    if (!editor || !selectedText.trim() || !selectionRange) return;

    // Check character limit before proceeding (only for gemini-proxy provider)
    if (process.env.REACT_APP_INFERENCE_PROVIDER === 'gemini-proxy' && selectedText.length > 1000) {
      alert(`You've selected ${selectedText.length} characters, but the limit is 1000 characters. Please select less text and try again.`);
      return;
    }

    setIsGenerating(true);

    try {
      // Build the system prompt using the function from Prompt.js
      const systemPrompt = buildSystemPrompt(userRole, selectedVersion, customContext);

      // Use current position (where user may have dragged the popup)
      const diffPosition = position;

      // Show diff view immediately with streaming state
      setDiffView({
        originalText: selectedText,
        newText: "",
        position: diffPosition,
        range: selectionRange,
        isStreaming: true
      });

      // Hide the selection popup
      setPopupState({
        basePosition: null,
        position: null,
        selectedText: "",
        selectionRange: null
      });

      // Accumulate and update the generated text as it streams
      let generatedText = "";

      await streamGeneration(
        systemPrompt,
        selectedText,
        (delta) => {
          generatedText += delta;
          // Update the diff view with streaming text
          setDiffView(prev => ({
            ...prev,
            newText: generatedText
          }));
        }
      );

      // Mark streaming as complete to show diff
      setDiffView(prev => ({
        ...prev,
        isStreaming: false
      }));

    } catch (error) {
      console.error("Error generating versions:", error);
      alert(`Failed to generate versions: ${error.message}`);
      setDiffView(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRejectChanges = () => {
    setDiffView(null);
    // Keep the original text selection state so user can try again
  };

  const calculateChangelogPosition = () => {
    const editorElement = document.querySelector('.ProseMirror');
    if (!editorElement) return null;

    const editorRect = editorElement.getBoundingClientRect();
    const gap = 10;
    const x = editorRect.right + gap;
    const y = editorRect.top;

    return { x, y };
  };

  const handleAcceptChanges = () => {
    if (!editor || !diffView) return;

    // Convert newlines back to <br> tags for HTML content
    const htmlContent = diffView.newText.replace(/\n/g, '<br>');

    editor
      .chain()
      .focus()
      .setTextSelection(diffView.range)
      .insertContent(htmlContent)
      .run();

    // Add to changelog
    const newChange = {
      id: Date.now(),
      timestamp: new Date(),
      originalText: diffView.originalText,
      newText: diffView.newText,
      context: customContext || 'No additional context'
    };
    setChanges(prev => [newChange, ...prev]);

    // Show changelog overlay and set position
    setShowChangelog(true);
    setChangelogPosition(calculateChangelogPosition());

    setDiffView(null);
    setPopupState({
      basePosition: null,
      position: null,
      selectedText: "",
      selectionRange: null
    });
  };

  const handleClearChangelog = () => {
    setChanges([]);
    setShowChangelog(false);
  };

  const handleToggleChangelog = () => {
    setShowChangelog(false);
    setChangelogPosition(null);
  };

  return (
    <div className="app-container">
      <div className="content-wrapper">
        <div className="header-section">
          <h1 className="header-title">Contract Editor</h1>
          <p className="header-description">
            Highlight text to generate versions, review changes with diff view, then accept or reject
          </p>

          {/* Contract Selector */}
          <div className="contract-selector-container">
            <span className="selector-label">Contract:</span>
            <select
              value={selectedContract}
              onChange={(e) => setSelectedContract(e.target.value)}
              className="contract-select"
            >
              {Object.entries(CONTRACTS).map(([key, contract]) => (
                <option key={key} value={key}>
                  {contract.name}
                </option>
              ))}
            </select>
          </div>

          {/* Role Selector */}
          <div className="role-selector-container">
            <span className="selector-label">User Role:</span>
            <div className="role-button-group">
              {CONTRACTS[selectedContract]?.roles.map((role) => (
                <button
                  key={role}
                  onClick={() => setUserRole(role)}
                  className={`role-button ${userRole === role ? 'active' : ''}`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* The editable area with flexbox layout */}
        <div className="editor-layout">
          <div className="editor-wrapper">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>

      {/* Text selection popup */}
      <TextSelectionPopup
        position={popupState.position}
        onGenerateVersions={handleGenerateVersions}
        isGenerating={isGenerating}
        customContext={customContext}
        onCustomContextChange={setCustomContext}
        onPositionChange={(newPosition) => setPopupState(prev => ({ ...prev, position: newPosition }))}
      />

      {/* Diff view for reviewing changes */}
      <DiffView
        originalText={diffView?.originalText}
        newText={diffView?.newText}
        position={diffView?.position}
        onAccept={handleAcceptChanges}
        onReject={handleRejectChanges}
        isStreaming={diffView?.isStreaming}
      />

      {/* Changelog overlay */}
      {showChangelog && changes.length > 0 && changelogPosition && (
        <Changelog
          changes={changes}
          position={changelogPosition}
          onClear={handleClearChangelog}
          onToggleCollapse={handleToggleChangelog}
          isCollapsed={false}
        />
      )}
    </div>
  );
}
